// Procedural pixel-character portraits (spec-captain §Runtime, portrait style
// 'pixelchar'). TypeScript port of the paint-by-numbers character algorithm
// from pixelart-characters (https://github.com/learnpixelart/pixelart/tree/master/pixelart-characters),
// which is dedicated to the public domain (CC0 1.0; README: "The scripts are
// dedicated to the public domain"). What is ported: the composition model —
// a character type holds ordered parts (face → clothes → eye → mouth → beard
// → hair → headwear), a seeded lottery picks one variant per part (0 skips an
// optional part), designs are ASCII grids whose color slots ('S', 'H', 'U'…)
// resolve through per-part color tables, and parts are pasted in order onto
// an empty canvas. The sprite designs themselves are original to Lumen Abyss
// and drawn on the game's 16-color palette (+ the captain skin ramp), so the
// cast reads as one game. Deterministic: same seed → same captain, always.

export const PIXELCHAR_GRID = 24;

// ── Palette slots ─────────────────────────────────────────────────────────
// Fixed inks available to every design (subset of the Lumen 16).
const INK_COLORS: Readonly<Record<string, string>> = {
  K: '#050a14', // abyss outline
  W: '#f4f4f4', // pure white (teeth, eye whites, medic cloth)
  C: '#35e0ff', // lumen cyan (lenses, glow bands)
  G: '#ffd36b', // surge gold (emblems)
  L: '#ff4d6b', // signal red (lips)
  N: '#29366f' // abyss indigo (hood cloth)
};

// Skin ramp (captain/compositor.ts SKIN_TONES + one deep shadow for the
// darkest tone). 'S' = base, 's' = shadow; the face attribute's color table.
const SKIN_TONES: readonly Readonly<Record<string, string>>[] = [
  { S: '#ffcd75', s: '#ef7d57' },
  { S: '#ef7d57', s: '#c97a5e' },
  { S: '#c97a5e', s: '#8a5a3b' },
  { S: '#8a5a3b', s: '#5d3a24' }
];

// Hair color tables ('H' = base, 'h' = highlight); shared by hair and beard.
const HAIR_COLORS: readonly Readonly<Record<string, string>>[] = [
  { H: '#29366f', h: '#566c86' }, // void navy (kept off the near-black UI frames)
  { H: '#566c86', h: '#9fefff' }, // steel
  { H: '#ff6b3d', h: '#ffd36b' }, // ember
  { H: '#ff3d81', h: '#ff6b3d' }, // magenta
  { H: '#ffd36b', h: '#f4f4f4' }, // gold
  { H: '#3dffc4', h: '#a7f070' } // mint
];

// ── Part designs ──────────────────────────────────────────────────────────
// Each design is a list of [y, row] pairs on a 24×24 grid; '.' is
// transparent. Row strings are exactly 24 chars. This mirrors the gem's
// Image.parse(txt, colors:) format.

type PartRows = ReadonlyArray<readonly [number, string]>;

interface PartVariant {
  readonly rows: PartRows;
  /** Per-variant color table (e.g. uniform cloth 'U'/accent 'u'). */
  readonly colors?: Readonly<Record<string, string>>;
}

const FACE_VARIANTS: Record<'male' | 'female', PartVariant> = {
  male: {
    rows: [
      [3, '........KKKKKKKK........'],
      [4, '.......KSSSSSSSSK.......'],
      [5, '......KSSSSSSSSSSK......'],
      [6, '......KSSSSSSSSSSK......'],
      [7, '......KSSSSSSSSSSK......'],
      [8, '......KSSSSSSSSSsK......'],
      [9, '......KSSSSSSSSSsK......'],
      [10, '......KSSSSSSSSSsK......'],
      [11, '......KSSSSssSSSsK......'],
      [12, '......KSSSSSSSSSsK......'],
      [13, '.......KSSSSSSSsK.......'],
      [14, '........KSSSSSsK........'],
      [15, '.........KKKKKK.........'],
      [16, '.........KSSSSK.........']
    ]
  },
  female: {
    rows: [
      [3, '........KKKKKKKK........'],
      [4, '.......KSSSSSSSSK.......'],
      [5, '......KSSSSSSSSSSK......'],
      [6, '......KSSSSSSSSSSK......'],
      [7, '......KSSSSSSSSSsK......'],
      [8, '......KSSSSSSSSSsK......'],
      [9, '......KSSSSSSSSSsK......'],
      [10, '......KSSSSSSSSSsK......'],
      [11, '......KSSSSssSSSsK......'],
      [12, '.......KSSSSSSSsK.......'],
      [13, '........KSSSSSsK........'],
      [14, '.........KSSSSK.........'],
      [15, '..........KKKK..........'],
      [16, '..........KSSK..........']
    ]
  }
};

const CLOTHES_VARIANTS: readonly PartVariant[] = [
  {
    // Teal ops suit, cyan collar flash + chest lights.
    colors: { U: '#1e5b7a', u: '#35e0ff' },
    rows: [
      [16, '......UUUU....UUUU......'],
      [17, '.....UUUUUUuuUUUUUU.....'],
      [18, '....UUUUUUUUUUUUUUUU....'],
      [19, '....UUUUuUUUUUUuUUUU....'],
      [20, '....UUUUUUUUUUUUUUUU....'],
      [21, '....UUUUUUUUUUUUUUUU....'],
      [22, '....UUUUUUUUUUUUUUUU....'],
      [23, '....UUUUUUUUUUUUUUUU....']
    ]
  },
  {
    // Indigo officer, gold epaulettes + button line.
    colors: { U: '#29366f', u: '#ffd36b' },
    rows: [
      [16, '......UUUU....UUUU......'],
      [17, '.....uuUUUUUUUUUUuu.....'],
      [18, '....UUUUUUUuuUUUUUUU....'],
      [19, '....UUUUUUUuuUUUUUUU....'],
      [20, '....UUUUUUUuuUUUUUUU....'],
      [21, '....UUUUUUUUUUUUUUUU....'],
      [22, '....UUUUUUUUUUUUUUUU....'],
      [23, '....UUUUUUUUUUUUUUUU....']
    ]
  },
  {
    // Steel hazmat, sealed collar + mint light bar.
    colors: { U: '#566c86', u: '#3dffc4' },
    rows: [
      [16, '......UUUUUUUUUUUU......'],
      [17, '.....UUUUUUUUUUUUUU.....'],
      [18, '....UUUUUuuuuuuUUUUU....'],
      [19, '....UUUUUUUUUUUUUUUU....'],
      [20, '....UUUUUUUUUUUUUUUU....'],
      [21, '....UUUUUUUUUUUUUUUU....'],
      [22, '....UUUUUUUUUUUUUUUU....'],
      [23, '....UUUUUUUUUUUUUUUU....']
    ]
  },
  {
    // Void runner, diagonal magenta strap.
    colors: { U: '#0a1b2e', u: '#ff3d81' },
    rows: [
      [16, '......UUUU....UUUU......'],
      [17, '.....UUUUUUUUUUUUUU.....'],
      [18, '....UUUUuUUUUUUUUUUU....'],
      [19, '....UUUUUuUUUUUUUUUU....'],
      [20, '....UUUUUUuUUUUUUUUU....'],
      [21, '....UUUUUUUuUUUUUUUU....'],
      [22, '....UUUUUUUUUUUUUUUU....'],
      [23, '....UUUUUUUUUUUUUUUU....']
    ]
  },
  {
    // White medic, red cross.
    colors: { U: '#f4f4f4', u: '#ff4d6b' },
    rows: [
      [16, '......UUUU....UUUU......'],
      [17, '.....UUUUUUUUUUUUUU.....'],
      [18, '....UUUUUUUuUUUUUUUU....'],
      [19, '....UUUUUuuuuUUUUUUU....'],
      [20, '....UUUUUUUuUUUUUUUU....'],
      [21, '....UUUUUUUUUUUUUUUU....'],
      [22, '....UUUUUUUUUUUUUUUU....'],
      [23, '....UUUUUUUUUUUUUUUU....']
    ]
  },
  {
    // Ember engineer, dark utility straps.
    colors: { U: '#ff6b3d' },
    rows: [
      [16, '......UUUU....UUUU......'],
      [17, '.....UUUUUUUUUUUUUU.....'],
      [18, '....UUUUUUUUUUUUUUUU....'],
      [19, '....UUKUUUUUUUUUUKUU....'],
      [20, '....UUKUUUUUUUUUUKUU....'],
      [21, '....UUUUUUUUUUUUUUUU....'],
      [22, '....UUUUUUUUUUUUUUUU....'],
      [23, '....UUUUUUUUUUUUUUUU....']
    ]
  }
];

const EYE_VARIANTS: readonly PartVariant[] = [
  {
    // Round.
    rows: [
      [9, '........KK....KK........'],
      [10, '........KK....KK........']
    ]
  },
  {
    // Narrow, determined.
    rows: [[9, '........KKK..KKK........']]
  },
  {
    // Cyber visor, cyan lenses.
    rows: [
      [9, '.......KCCKKKKCCK.......'],
      [10, '.......KCCKKKKCCK.......']
    ]
  },
  {
    // Lashes.
    rows: [
      [8, '.......K........K.......'],
      [9, '........KK....KK........'],
      [10, '........KK....KK........']
    ]
  },
  {
    // Wide, whites + pupils.
    rows: [
      [9, '........WW....WW........'],
      [10, '........WK....KW........']
    ]
  },
  {
    // Sleepy half-lids.
    rows: [
      [8, '........ss....ss........'],
      [9, '........KK....KK........']
    ]
  }
];

const MOUTH_VARIANTS: readonly PartVariant[] = [
  { rows: [[13, '..........KKKK..........']] }, // neutral
  {
    // Smile.
    rows: [
      [13, '..........K..K..........'],
      [14, '...........KK...........']
    ]
  },
  {
    // Teeth grin.
    rows: [
      [13, '..........KKKK..........'],
      [14, '..........KWWK..........']
    ]
  },
  {
    // Open (wary).
    rows: [
      [13, '...........KK...........'],
      [14, '...........KK...........']
    ]
  },
  {
    // Lips.
    rows: [
      [13, '..........LLLL..........'],
      [14, '...........LL...........']
    ]
  }
];

// Optional part: lottery index 0 = clean-shaven (the gem's num==0 skip rule).
const BEARD_VARIANTS: readonly PartVariant[] = [
  {
    // Full beard.
    rows: [
      [13, '.......HH......HH.......'],
      [14, '........HHHHHHHH........'],
      [15, '.........HHHHHH.........'],
      [16, '..........HHHH..........']
    ]
  },
  {
    // Mustache.
    rows: [
      [12, '..........HHHH..........'],
      [13, '.........HH..HH.........']
    ]
  },
  {
    // Goatee.
    rows: [
      [15, '..........HHHH..........'],
      [16, '...........HH...........']
    ]
  }
];

const HAIR_VARIANTS: readonly PartVariant[] = [
  {
    // Buzz cut.
    rows: [
      [2, '........HHhhHHHH........'],
      [3, '.......HHHHHHHHHH.......'],
      [4, '.......HH......HH.......'],
      [5, '.......H........H.......']
    ]
  },
  {
    // Side sweep.
    rows: [
      [1, '........HHHHHHH.........'],
      [2, '.......HhhHHHHHHH.......'],
      [3, '......HHHHHHHHHHH.......'],
      [4, '......HHHHH.....H.......'],
      [5, '......H.........H.......']
    ]
  },
  {
    // Mohawk (base sits on the crown so hoods cover it cleanly).
    rows: [
      [0, '...........HH...........'],
      [1, '...........HH...........'],
      [2, '..........HHHH..........'],
      [3, '..........HHHh..........']
    ]
  },
  {
    // Long bob.
    rows: [
      [1, '........HHHHHHHH........'],
      [2, '......HHhhHHHHHHHH......'],
      [3, '......HHHHHHHHHHHH......'],
      [4, '......HH........HH......'],
      [5, '......HH........HH......'],
      [6, '......HH........HH......'],
      [7, '......HH........HH......'],
      [8, '......HH........HH......'],
      [9, '......HH........HH......'],
      [10, '......HH........HH......']
    ]
  },
  {
    // Ponytail.
    rows: [
      [1, '........HHHHHHHH........'],
      [2, '.......HHhhHHHHHHH......'],
      [3, '.......HHHHHHHHHHHH.....'],
      [4, '.......H.........HH.....'],
      [5, '.......H.........HH.....'],
      [6, '..................HH....'],
      [7, '..................HH....'],
      [8, '..................HH....'],
      [9, '..................HH....'],
      [10, '.................HH.....']
    ]
  },
  {
    // Spiky.
    rows: [
      [0, '........H..HH..H........'],
      [1, '........HHHHHHHH........'],
      [2, '.......HHhhHHHHHH.......'],
      [3, '.......HHHHHHHHHH.......'],
      [4, '.......H........H.......']
    ]
  },
  {
    // Bald with side growth.
    rows: [
      [5, '......HH........HH......'],
      [6, '......H..........H......'],
      [7, '......H..........H......'],
      [8, '......H..........H......']
    ]
  },
  {
    // Afro.
    rows: [
      [0, '.........HHHHHH.........'],
      [1, '.......HHhhHHHHHH.......'],
      [2, '......HHHHHHHHHHHH......'],
      [3, '......HHHHHHHHHHHH......'],
      [4, '......HH........HH......']
    ]
  }
];

// Optional part: lottery index 0 = bare-headed.
const HEADWEAR_VARIANTS: readonly PartVariant[] = [
  {
    // Peaked captain's cap, gold emblem.
    rows: [
      [0, '.........KKKKKK.........'],
      [1, '.......KKKKKKKKKK.......'],
      [2, '.......KKKKGGKKKK.......'],
      [3, '......KKKKKKKKKKKK......']
    ]
  },
  {
    // Glow headband.
    rows: [
      [4, '......CCCCCCCCCCCC......'],
      [5, '......C..........C......']
    ]
  },
  {
    // Abyss hood (domed so tall hairstyles don't poke through the top).
    rows: [
      [0, '........NNNNNNNN........'],
      [1, '........NNNNNNNN........'],
      [2, '......NNNNNNNNNNNN......'],
      [3, '......NNNNNNNNNNNN......'],
      [4, '......NN........NN......'],
      [5, '......NN........NN......'],
      [6, '......NN........NN......'],
      [7, '......NN........NN......']
    ]
  },
  {
    // Comms headset with mic boom.
    rows: [
      [2, '........KKKKKKKK........'],
      [3, '.......K........K.......'],
      [4, '......KK........KK......'],
      [5, '......KK........KK......'],
      [6, '......KK........KK......'],
      [7, '..................KK....'],
      [8, '................KK......'],
      [9, '..............KC........']
    ]
  }
];

const DESIGN_SLOTS = new Set(['K', 'W', 'C', 'G', 'L', 'N', 'S', 's', 'H', 'h', 'U', 'u', '.']);

// Design-table guard: every row must sit on the 24×24 grid with known color
// slots only. Art data must never crash the game: import-time lint only logs
// (render also clamps defensively in paste()); tests assert a clean lint.
export function lintPixelcharDesigns(): string[] {
  const problems: string[] = [];
  const groups: readonly PartVariant[] = [
    FACE_VARIANTS.male,
    FACE_VARIANTS.female,
    ...CLOTHES_VARIANTS,
    ...EYE_VARIANTS,
    ...MOUTH_VARIANTS,
    ...BEARD_VARIANTS,
    ...HAIR_VARIANTS,
    ...HEADWEAR_VARIANTS
  ];
  for (const variant of groups) {
    for (const [y, row] of variant.rows) {
      if (y < 0 || y >= PIXELCHAR_GRID || row.length !== PIXELCHAR_GRID) {
        problems.push(`pixelchar design row out of grid: y=${y} len=${row.length}`);
        continue;
      }
      for (const ch of row) {
        if (!DESIGN_SLOTS.has(ch)) problems.push(`pixelchar design uses unknown slot '${ch}'`);
      }
    }
  }
  return problems;
}

const lintProblems = lintPixelcharDesigns();
if (lintProblems.length > 0) {
  console.error('[pixelchar] malformed design rows (rendering clamped):', lintProblems);
}

// ── Seeded lottery ────────────────────────────────────────────────────────

export interface PixelCharacterSpec {
  /** All lottery picks packed into one small integer. */
  variantId: number;
  /** Face design family (the gem's character type). */
  type: 'male' | 'female';
  /** Index into the skin ramp (the face attribute's color table). */
  skinTone: number;
  parts: {
    clothes: number;
    eye: number;
    mouth: number;
    /** 0 = clean-shaven (optional part skipped; always 0 for the female
     * type — the gem flags beard attributes 'm', see pixelchar.ts). */
    beard: number;
    hair: number;
    /** Shared hair/beard color table index. */
    hairColor: number;
    /** 0 = bare-headed (optional part skipped). */
    headwear: number;
  };
}

// Exact mulberry32 per GDD Appendix A §A.1 (inline copy, same rationale as
// captain/seed.ts — the captain feature must not depend on the sim).
function mulberry32(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6D2B79F5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * The seeded lottery: one mulberry32 stream draws, in fixed order, the
 * character type, skin tone and one variant per part (0 keeps optional
 * parts empty). Pure; same seed → same spec, always.
 */
export function pixelCharacterSeed(seed: number): PixelCharacterSpec {
  const rng = mulberry32(seed);
  const int = (n: number): number => Math.floor(rng() * n);

  const type = int(2) === 0 ? 'male' : 'female';
  const skinTone = int(SKIN_TONES.length);
  const clothes = int(CLOTHES_VARIANTS.length);
  const eye = int(EYE_VARIANTS.length);
  const mouth = int(MOUTH_VARIANTS.length);
  // Optional parts carry the gem's m/f type flags: beards are male-only
  // (the female type has an empty dummy beard part), so the female lottery
  // always lands on the skipped 0.
  const beard = type === 'male' ? int(BEARD_VARIANTS.length + 1) : 0;
  const hair = int(HAIR_VARIANTS.length);
  const hairColor = int(HAIR_COLORS.length);
  const headwear = int(HEADWEAR_VARIANTS.length + 1); // 0 = none

  const variantId =
    ((type === 'female' ? 1 : 0) |
      (skinTone << 1) |
      (clothes << 3) |
      (eye << 6) |
      (mouth << 9) |
      (beard << 12) |
      (hair << 14) |
      (hairColor << 17) |
      (headwear << 20)) >>>
    0;

  return {
    variantId,
    type,
    skinTone,
    parts: { clothes, eye, mouth, beard, hair, hairColor, headwear }
  };
}

// ── Compositor (paint by numbers) ─────────────────────────────────────────

/**
 * Composite the character into a 24×24 pixel buffer (hex colors or null for
 * transparent), parts pasted in the gem's order: face → clothes → eye →
 * mouth → beard → hair → headwear. Pure; exported for tests and tooling.
 */
export function renderPixelCharacter(spec: PixelCharacterSpec): (string | null)[] {
  const grid: (string | null)[] = new Array(PIXELCHAR_GRID * PIXELCHAR_GRID).fill(null);
  // Color context: fixed inks + this captain's skin and hair tables, exactly
  // like the gem's Image.parse(txt, colors: attribute[:colors]).
  const colors: Record<string, string> = {
    ...INK_COLORS,
    ...SKIN_TONES[spec.skinTone % SKIN_TONES.length],
    ...HAIR_COLORS[spec.parts.hairColor % HAIR_COLORS.length]
  };

  const paste = (variant: PartVariant): void => {
    const table = variant.colors ? { ...colors, ...variant.colors } : colors;
    for (const [y, row] of variant.rows) {
      // Defensive clamp: a malformed row must never write out of grid (the
      // import lint reports it; the game keeps rendering the rest).
      if (y < 0 || y >= PIXELCHAR_GRID) continue;
      const width = Math.min(row.length, PIXELCHAR_GRID);
      for (let x = 0; x < width; x++) {
        const ch = row[x]!;
        if (ch === '.') continue;
        const color = table[ch];
        if (color) grid[y * PIXELCHAR_GRID + x] = color;
      }
    }
  };

  paste(FACE_VARIANTS[spec.type]);
  paste(CLOTHES_VARIANTS[spec.parts.clothes % CLOTHES_VARIANTS.length]!);
  paste(EYE_VARIANTS[spec.parts.eye % EYE_VARIANTS.length]!);
  paste(MOUTH_VARIANTS[spec.parts.mouth % MOUTH_VARIANTS.length]!);
  if (spec.parts.beard > 0) paste(BEARD_VARIANTS[(spec.parts.beard - 1) % BEARD_VARIANTS.length]!);
  paste(HAIR_VARIANTS[spec.parts.hair % HAIR_VARIANTS.length]!);
  if (spec.parts.headwear > 0) {
    paste(HEADWEAR_VARIANTS[(spec.parts.headwear - 1) % HEADWEAR_VARIANTS.length]!);
  }
  return grid;
}

const rgbCache = new Map<string, [number, number, number]>();

function rgbOf(hex: string): [number, number, number] {
  let rgb = rgbCache.get(hex);
  if (!rgb) {
    rgb = [
      parseInt(hex.slice(1, 3), 16),
      parseInt(hex.slice(3, 5), 16),
      parseInt(hex.slice(5, 7), 16)
    ];
    rgbCache.set(hex, rgb);
  }
  return rgb;
}

/**
 * Draw the seeded character pixel-perfect at its native 24×24 grid onto
 * `canvas` (resized to the grid; smoothing off — the caller scales).
 * Deterministic: same seed = same person every time.
 */
export function drawPixelCharacter(seed: number, canvas: HTMLCanvasElement): void {
  const pixels = renderPixelCharacter(pixelCharacterSeed(seed));
  canvas.width = PIXELCHAR_GRID;
  canvas.height = PIXELCHAR_GRID;
  const ctx = canvas.getContext('2d')!;
  ctx.imageSmoothingEnabled = false;
  const image = ctx.createImageData(PIXELCHAR_GRID, PIXELCHAR_GRID);
  const data = image.data;
  for (let i = 0; i < pixels.length; i++) {
    const color = pixels[i];
    if (!color) continue;
    const [r, g, b] = rgbOf(color);
    data[i * 4] = r;
    data[i * 4 + 1] = g;
    data[i * 4 + 2] = b;
    data[i * 4 + 3] = 255;
  }
  ctx.putImageData(image, 0, 0);
}
